import axios from "axios";
import { SERVER_BASE_URL } from "../utils/constants";
import { getFlowPreviewHost } from "../utils/fanFlowNavigation";

function trimTrailingSlash(s: string): string {
  return s.replace(/\/+$/, "");
}

/**
 * Resolve the API origin for `instance` on every request.
 *
 * `constants.ts` reads `window.appConfig` once at module load; if that runs before
 * `public/config/config.js`, `SERVER_BASE_URL` is empty. Axios then treats `api/moment` as
 * relative to the fan page origin → POST hits static/S3 and returns XML `MethodNotAllowed`.
 * Re-read `window.appConfig` here and fall back to the usual `*-client` → `*-api` hostname rule.
 */
function resolveApiBaseUrl(): string {
  const fromConstants = trimTrailingSlash(String(SERVER_BASE_URL || ""));
  if (fromConstants) return fromConstants;
  if (typeof window !== "undefined") {
    const cfg = (window as unknown as { appConfig?: { apiUrl?: string } }).appConfig;
    const fromWindow = trimTrailingSlash(String(cfg?.apiUrl || ""));
    if (fromWindow) return fromWindow;
    const { protocol, hostname } = window.location;
    const sub = /^(.+)-client(\.[a-z0-9.-]+)$/i.exec(hostname);
    if (sub) {
      return `${protocol}//${sub[1]}-api${sub[2]}`;
    }
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      const p = typeof process !== "undefined" && process.env.REACT_APP_API_URL
        ? String(process.env.REACT_APP_API_URL).trim()
        : "";
      return trimTrailingSlash(p);
    }
  }
  return "";
}

export const instance = axios.create({ baseURL: resolveApiBaseUrl() });

instance.interceptors.request.use((config) => {
  const base = resolveApiBaseUrl();
  if (base) {
    config.baseURL = base;
  }
  const previewHost = getFlowPreviewHost();
  if (previewHost) {
    config.headers = config.headers || {};
    (config.headers as Record<string, string>)["X-Momento-Fan-Host"] = previewHost;
  }
  return config;
});

export const setBearerToken = (token: string): void => {
  if (!token || typeof token !== "string") {
    console.warn("> Bearer Token skipped: invalid token");
    return;
  }
  console.log(`> Bearer Token has been set to: ${token.slice(0, 10)}...`);
  instance.defaults.headers.common = { Authorization: `bearer ${token}` };
};

export const clearBearerToken = (): void => {
  instance.defaults.headers.common = null;
};
